/**
 * 交通費の月次再計算(P-38。2026-09-16 実装)。
 *
 * 1日の交通費は取込のたびに「その日の実稼働」で決まるが(0/30/50%)、**その月の実稼働(品川)が100時間以上になった
 * 時点で、その月に稼働した全日を70%に計算し直す**。月をまたいで判定できないので、取込のあとにこの処理を通す。
 *
 * 同じ人が品川の登録を2つ(給湯器・ホワイト)持つ日は**合算して判定する**(2026-09-16 発注者判断)。
 * マスタでは「小原　南風」と「小原　南風W」のように**別のオペレーターとして登録されている場合がある**ため、
 * 氏名(空白と末尾のW/Ｗを除く)で同じ人とみなす。
 */
import { commuteAmount } from '@core/lib/commute';
import type { DailySummary, StaffDaily } from '@core/lib/types';

/** 同じ人とみなすキー(空白と末尾の W/Ｗ を落とした氏名) */
export function personKey(s: Pick<StaffDaily, 'name' | 'op_id'>): string {
  const name = (s.name ?? '').replace(/[\s　]/g, '').replace(/[WＷ]+$/, '');
  return name || s.op_id;
}

const isShinagawa = (s: StaffDaily) => s.work_style === 'shinagawa';

/** その日の「人ごとの品川の実稼働時間」 */
export function shinagawaHoursByPerson(staff: StaffDaily[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const s of staff) {
    if (!isShinagawa(s)) continue;
    out.set(personKey(s), (out.get(personKey(s)) ?? 0) + (s.worked_hours ?? 0));
  }
  return out;
}

type DaySummary = Pick<DailySummary, 'staff'> & { id: string };

/**
 * 1か月分の daily_summary の交通費を計算し直す。**中身は変えず、変わった日だけ返す**(呼び出し側が保存する)。
 * `commute_base`(マスタの交通費)が入っていない古い行は触らない。
 */
export function settleCommuteForMonth<T extends DaySummary>(days: T[]): { id: string; staff: StaffDaily[] }[] {
  const monthHours = new Map<string, number>();
  for (const d of days) for (const [key, h] of shinagawaHoursByPerson(d.staff ?? [])) monthHours.set(key, (monthHours.get(key) ?? 0) + h);

  const changed: { id: string; staff: StaffDaily[] }[] = [];
  for (const d of days) {
    const dayHours = shinagawaHoursByPerson(d.staff ?? []);
    let dirty = false;
    const staff = (d.staff ?? []).map((s) => {
      const base = s.commute_base ?? 0;
      if (!isShinagawa(s) || base <= 0) return s;
      const key = personKey(s);
      const next = commuteAmount(base, dayHours.get(key) ?? 0, monthHours.get(key) ?? 0);
      if (next === s.commute) return s;
      dirty = true;
      return { ...s, commute: next, pay: s.pay - s.commute + next };
    });
    if (dirty) changed.push({ id: d.id, staff });
  }
  return changed;
}
